/**
 * CO-review API. Wraps the real backend endpoints documented in
 * rms-integration-cdk/lib/lambda/report-review/REPORT_REVIEW_API_REFERENCE.md.
 *
 * All routes are gated server-side on the `co-approval-workflow` feature flag.
 * Frontend callers should additionally check `useCoApprovalEnabled()` before
 * calling these to avoid pointless 403s.
 */

import rmsClient from "./clients/rmsClient";
import type {
  CoReviewComment,
  NewCoReviewCommentInput,
} from "../shared/interfaces/CoReviewComment";
import type { PendingCoComment } from "./coReviewApiTypes";
import {
  type ServerComment,
  buildCycleByVersionNumber,
  decodeServerComment,
  encodeCommentForServer,
} from "./coReviewCommentAdapter";
import { getUserName } from "./userDirectory";

export type { PendingCoComment } from "./coReviewApiTypes";

interface ListCommentsResponse {
  reportId: string;
  comments: ServerComment[];
}

interface SubmitReviewResponse {
  reportId: string;
  reportStatus: string;
  commentsCreated: number;
}

function decodeMany(comments: ServerComment[]): CoReviewComment[] {
  const cycleByVersionNumber = buildCycleByVersionNumber(comments);
  const decoded = comments.map((c) =>
    decodeServerComment(c, {
      resolveAuthorName: getUserName,
      cycleByVersionNumber,
    }),
  );
  // Replies are stored with the artifact version current at write time, which
  // can be newer than the parent's version (e.g. CO retracts, then replies on
  // a bumped artifact). For display, inherit the parent's reviewCycle so a
  // thread renders together in the cycle the original feedback belongs to.
  const byId = new Map(decoded.map((c) => [c.commentId, c] as const));
  return decoded.map((c) => {
    if (!c.parentCommentId) return c;
    const parent = byId.get(c.parentCommentId);
    if (!parent || parent.reviewCycle === c.reviewCycle) return c;
    return { ...c, reviewCycle: parent.reviewCycle };
  });
}

export interface CoApproveInput {
  reportId: string;
}

export async function coApprove({ reportId }: CoApproveInput): Promise<void> {
  await rmsClient.post<SubmitReviewResponse>(
    `/reports/${encodeURIComponent(reportId)}/review`,
    { approval: true },
  );
}

export interface CoRetractInput {
  reportId: string;
}

interface RetractResponse {
  reportId: string;
  reportStatus: string;
}

export async function coRetract({ reportId }: CoRetractInput): Promise<void> {
  await rmsClient.post<RetractResponse>(
    `/reports/${encodeURIComponent(reportId)}/review/retract`,
    {},
  );
}

export interface CoRequestChangesInput {
  reportId: string;
  comments: PendingCoComment[];
}

export async function coRequestChanges({
  reportId,
  comments,
}: CoRequestChangesInput): Promise<void> {
  // Empty comments is allowed: re-rejecting after a retract sends the same
  // existing feedback back to the officer without staging anything new.
  await rmsClient.post<SubmitReviewResponse>(
    `/reports/${encodeURIComponent(reportId)}/review`,
    {
      approval: false,
      ...(comments.length
        ? { comments: comments.map(encodeCommentForServer) }
        : {}),
    },
  );
}

export interface GetCoCommentsOptions {
  cycle?: "current" | "all";
  sectionPath?: string;
}

export async function getCoComments(
  reportId: string,
  opts: GetCoCommentsOptions = {},
): Promise<CoReviewComment[]> {
  // Server handles cycle=current by returning the latest cycle plus the
  // parents of any reply in that cycle, so threads aren't orphaned when a
  // reply is authored against a newer artifact version than its parent
  // (see REPORT_REVIEW_API_REFERENCE.md §5.5).
  const params = opts.cycle === "current" ? { cycle: "current" } : undefined;
  const res = await rmsClient.get<ListCommentsResponse>(
    `/reports/${encodeURIComponent(reportId)}/comments`,
    { params },
  );
  let decoded = decodeMany(res.data?.comments ?? []);
  if (opts.sectionPath) {
    decoded = decoded.filter((c) => c.sectionPath === opts.sectionPath);
  }
  decoded.sort((a, b) => {
    if (a.sectionPath !== b.sectionPath)
      return a.sectionPath.localeCompare(b.sectionPath);
    return a.createdAt.localeCompare(b.createdAt);
  });
  return decoded;
}

export type CreateCoCommentInput = NewCoReviewCommentInput;

export async function createCoComment(
  input: CreateCoCommentInput,
): Promise<CoReviewComment> {
  const encoded = encodeCommentForServer({
    sectionPath: input.sectionPath,
    fieldLabel: input.fieldLabel,
    commentText: input.commentText,
    isSectionRejection: input.isSectionRejection,
    narrativeIndex: input.narrativeIndex,
    charStart: input.charStart,
    charEnd: input.charEnd,
    quotedText: input.quotedText,
    parentCommentId: input.parentCommentId ?? undefined,
    authorName: input.authorName,
  });
  const res = await rmsClient.post<ServerComment>(
    `/reports/${encodeURIComponent(input.reportId)}/comment`,
    encoded,
  );
  return decodeServerComment(res.data, { resolveAuthorName: getUserName });
}

export async function resolveCoComment(
  reportId: string,
  commentId: string,
): Promise<void> {
  await rmsClient.put<ServerComment>(
    `/reports/${encodeURIComponent(reportId)}/comment/${encodeURIComponent(commentId)}`,
    { resolved: true },
  );
}

export async function unresolveCoComment(
  reportId: string,
  commentId: string,
): Promise<void> {
  await rmsClient.put<ServerComment>(
    `/reports/${encodeURIComponent(reportId)}/comment/${encodeURIComponent(commentId)}`,
    { resolved: false },
  );
}
